import React, { useState, useEffect } from 'react';
import api from '../../api';
import { Link, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

/* =====================================================
   1. PRODUCT CARD 
   (Updated: Image fits container completely)
===================================================== */
const ProductCard = ({ product }) => {
  const history = useHistory();
  const [quantity, setQuantity] = useState(1);
  const [selectedPack, setSelectedPack] = useState(1); 
  const [adding, setAdding] = useState(false);
  const [inWishlist, setInWishlist] = useState(false);

  // --- Logic A: Data Normalization ---
  const productId = product.productId || product.id;
  const price = Number(product.productPrice || product.price || 0);
  const mrp = Number(product.productMRP || product.mrp || product.MRP || 0);
  const name = product.productName || "Unknown Product";
  const category = product.category || "General";
  
  // Dynamic Pack Size Logic
  const maxPackSize = product.productQuantity ? parseInt(product.productQuantity) : 3;
  const packs = Array.from({ length: maxPackSize > 0 ? maxPackSize : 3 }, (_, i) => i + 1);

  // Image handling
  const image = product.productPhotos && product.productPhotos.length > 0
      ? product.productPhotos[0].photoName || product.productPhotos[0]
      : "https://via.placeholder.com/300?text=No+Image";

  // Logic A: Price Calculations
  const currentPrice = price * selectedPack;
  const currentMRP = mrp * selectedPack;

  const increment = () => setQuantity(q => q + 1);
  const decrement = () => setQuantity(q => (q > 1 ? q - 1 : 1));

  // --- Logic A: Wishlist & Cart Handlers ---
  useEffect(() => {
    const stored = localStorage.getItem("wishlist");
    if (stored) {
      const list = JSON.parse(stored);
      if (list.find(item => item.productId === productId)) setInWishlist(true);
    }
  }, [productId]);

  const toggleWishlist = () => {
    let list = [];
    const stored = localStorage.getItem("wishlist");
    if (stored) list = JSON.parse(stored);

    if (inWishlist) {
      list = list.filter(item => item.productId !== productId);
      setInWishlist(false);
      toast.info("Removed from Wishlist");
    } else {
      list.push({ productId, productName: name, productPrice: price, image });
      setInWishlist(true);
      toast.success("Added to Wishlist");
    }
    localStorage.setItem("wishlist", JSON.stringify(list));
    setInWishlist(!inWishlist);
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  const handleAddToCart = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId || userId === "0") {
      toast.info("Please Log in to add items!");
      history.push("/login");
      return;
    }
    try {
      setAdding(true);
      const finalQty = quantity * selectedPack;
      await api.post('/usercart/add-product', null, {
        params: { userId, productId, orderQuantity: finalQty }
      });
      toast.success(`Added ${finalQty} ${name}(s) to cart`);
    } catch (error) {
      console.error("Cart Error:", error);
      toast.error("Failed to add item");
    } finally {
      setAdding(false);
    }
  };

  // --- UI: Source B Visual Structure ---
  return (
    <div className="col-lg-4 col-md-6 col-sm-6 mb-4">
      <div className="card h-100 shadow-sm" style={{ borderRadius: '15px', border: '1px solid #eee', overflow: 'hidden' }}>
        
        {/* Wishlist Button */}
        <button onClick={toggleWishlist} style={{
            position: 'absolute', top: '15px', right: '15px', background: 'white', border: 'none',
            borderRadius: '50%', width: '35px', height: '35px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10
          }}>
           <span style={{ fontSize: '20px', color: inWishlist ? 'red' : '#ccc', transition: '0.2s' }}>
             {inWishlist ? '♥' : '♡'}
           </span>
        </button>

        <Link to={`/product/${productId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
          
          {/* ➤ UPDATED IMAGE CONTAINER: Use 'cover' to eliminate whitespace */}
          <div style={{ 
              textAlign: 'center', 
              padding: '0', 
              backgroundColor: '#fff', 
              height: '280px', // Fixed height for uniformity
              width: '100%',   // Ensure full width
              display:'flex', 
              alignItems:'center', 
              justifyContent:'center',
              borderBottom: '1px solid #f9f9f9',
              overflow: 'hidden' // Hide potential overflows from 'cover'
          }}>
            <img src={image} alt={name} 
              onError={(e) => { e.target.src = "https://via.placeholder.com/300?text=No+Image"; }}
              style={{ 
                  height: '100%', 
                  width: '100%', 
                  objectFit: 'cover', // ➤ CHANGED: 'cover' fills the space, 'contain' leaves gaps
                  objectPosition: 'center'
              }}
            />
          </div>

          <div className="p-3">
            <span className="badge bg-light text-muted border mb-2" style={{fontSize: '11px', fontWeight: '500'}}>{category}</span>
            <h6 style={{ fontWeight: '700', fontSize: '16px', marginBottom: '5px', minHeight: '44px', lineHeight: '1.4', overflow:'hidden', textOverflow:'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {name}
            </h6>
            
            {/* Rating Stars */}
            <div className="mb-2">
                {[...Array(5)].map((_, i) => <span key={i} style={{ color: '#FFC107', fontSize: '14px' }}>★</span>)}
            </div>

            {/* Price Display */}
            <div className="d-flex align-items-center gap-2">
                <span style={{ fontSize: '18px', fontWeight: '800', color: '#222' }}>₹{currentPrice.toFixed(2)}</span>
                {currentMRP > 0 && currentMRP > currentPrice && (
                    <span style={{ textDecoration: 'line-through', color: '#999', fontSize: '13px' }}>₹{currentMRP.toFixed(2)}</span>
                )}
            </div>
          </div>
        </Link>

        {/* Footer Action Section */}
        <div className="p-3 pt-0 mt-auto bg-white border-0">
            <p className="mb-1 text-muted" style={{ fontSize: '11px', fontWeight:'600' }}>PACK SIZE</p>
            
            {/* Dynamic Pack Loop */}
            <div className="d-flex mb-3 flex-wrap gap-1">
                {packs.map(pack => (
                <div key={pack} onClick={() => setSelectedPack(pack)} style={{
                    border: selectedPack === pack ? '1px solid #333' : '1px solid #eee',
                    background: selectedPack === pack ? '#333' : '#fff',
                    color: selectedPack === pack ? '#fff' : '#333',
                    borderRadius: '4px', padding: '6px 0', 
                    flex: packs.length > 3 ? '0 0 30%' : '1', 
                    textAlign: 'center',
                    cursor: 'pointer', fontSize: '11px', fontWeight: '600'
                    }}>
                    x{pack}
                </div>
                ))}
            </div>

            <div className="d-flex justify-content-between align-items-center">
                <div style={{ border: '1px solid #eee', borderRadius: '20px', padding: '4px 10px', display: 'flex', alignItems: 'center' }}>
                    <button onClick={decrement} style={{ border: 'none', background: 'none', padding: '0 5px', fontWeight:'bold' }}>-</button>
                    <span className="mx-2" style={{fontSize: '13px', fontWeight: 'bold'}}>{quantity}</span>
                    <button onClick={increment} style={{ border: 'none', background: 'none', padding: '0 5px', fontWeight:'bold' }}>+</button>
                </div>
                <button onClick={handleAddToCart} disabled={adding} style={{
                    background: adding ? '#eee' : '#FFC35B', border: 'none', borderRadius: '20px',
                    padding: '8px 20px', fontWeight: 'bold', fontSize: '12px', 
                    color: '#222', boxShadow: '0 4px 10px rgba(255, 195, 91, 0.3)'
                }}>
                    {adding ? '...' : 'ADD TO CART'}
                </button>
            </div>
        </div>
      </div>
    </div>
  );
};

/* =====================================================
   2. SHOP PAGE 
   (Logic: Source A | UI: Source B)
===================================================== */
const DivineDepotShop = () => {
  // --- Logic A: State Management ---
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States (IDs for API)
  const [selectedCatId, setSelectedCatId] = useState(null); 
  const [selectedSubCatId, setSelectedSubCatId] = useState(null);
  
  // Helpers for Display (Names for Tags)
  const [selectedCatName, setSelectedCatName] = useState("All");
  const [selectedSubName, setSelectedSubName] = useState("");

  const [priceRange, setPriceRange] = useState(5000); 
  
  // --- Logic A: API Integration ---
  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        // Load Category Tree
        const catRes = await api.get('/category/tree');
        setCategories(catRes.data);
        // Initial Product Load
        await fetchFilteredProducts(null, null, 5000);
      } catch (err) {
        console.error("Error loading shop data:", err);
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, []);

  // Logic A: Server-Side Filtering
  const fetchFilteredProducts = async (catId, subId, maxPrice) => {
    setLoading(true);
    try {
      const params = { minPrice: 0, maxPrice: maxPrice };
      if (catId) params.categoryId = catId;
      if (subId) params.subcategoryId = subId;

      const res = await api.get('/products/filter', { params });
      setProducts(res.data);
    } catch (error) {
      console.error("Filter Error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Logic A: Handlers
  const handleCategoryClick = (cat) => {
    const idString = String(cat.categoryId);
    
    // Toggle Logic
    if(selectedCatId === idString) {
        handleReset(); // Clicking active category resets
        return;
    }

    setSelectedCatId(idString);
    setSelectedCatName(cat.categoryName);
    
    // Reset Subcat when changing Main Cat
    setSelectedSubCatId(null); 
    setSelectedSubName("");

    fetchFilteredProducts(idString, null, priceRange);
  };

  const handleSubCategoryClick = (e, cat, sub) => {
    e.stopPropagation();
    const catIdStr = String(cat.categoryId);
    const subIdStr = String(sub.subCategoryId);

    setSelectedCatId(catIdStr);
    setSelectedCatName(cat.categoryName);
    
    setSelectedSubCatId(subIdStr);
    setSelectedSubName(sub.subCategoryName);

    fetchFilteredProducts(catIdStr, subIdStr, priceRange);
  };

  const handlePriceChange = (e) => {
    setPriceRange(e.target.value);
  };

  const applyPriceFilter = () => {
     fetchFilteredProducts(selectedCatId, selectedSubCatId, priceRange);
  };

  const handleReset = () => {
    setSelectedCatId(null);
    setSelectedSubCatId(null);
    setSelectedCatName("All");
    setSelectedSubName("");
    setPriceRange(5000);
    fetchFilteredProducts(null, null, 5000);
  };

  // --- UI: Source B Layout & Styling ---
  return (
    <div className="container py-5">
      <div className="row">
        
        {/* ================= SIDEBAR (Sticky) ================= */}
        <div className="col-lg-3 mb-4">
          <div className="bg-white p-4 shadow-sm rounded border" style={{ position: 'sticky', top: '90px', zIndex: 90 }}>
            
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                <h5 style={{ margin: 0, fontWeight: '800', fontFamily: 'serif' }}>FILTERS</h5>
                <small 
                    onClick={handleReset} 
                    style={{ cursor: 'pointer', color: '#FFC35B', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '11px' }}
                >
                    RESET ALL
                </small>
            </div>

            {/* Price Filter */}
            <div className="mb-4">
                <h6 style={{ fontWeight: '700', fontSize: '12px', marginBottom: '15px', color: '#999', letterSpacing:'1px' }}>MAX PRICE</h6>
                <input type="range" className="form-range" min="0" max="5000" step="100" 
                    value={priceRange} onChange={handlePriceChange} onMouseUp={applyPriceFilter} onTouchEnd={applyPriceFilter}
                    style={{ accentColor: '#FFC35B' }} />
                <div className="d-flex justify-content-between mt-1" style={{ fontSize: '13px', color: '#555', fontWeight:'600' }}>
                    <span>₹0</span><span>₹{priceRange}</span>
                </div>
            </div>

            <hr style={{ borderColor: '#f1f1f1', margin: '20px 0' }} />

            {/* Categories */}
            <h6 style={{ fontWeight: '700', fontSize: '12px', marginBottom: '15px', color: '#999', letterSpacing:'1px' }}>CATEGORIES</h6>
            <ul className="list-unstyled">
                {/* View All Button */}
                <li onClick={handleReset} className="p-2 mb-2 rounded"
                    style={{ cursor: 'pointer', backgroundColor: !selectedCatId ? '#333' : 'transparent', color: !selectedCatId ? '#fff' : '#333', fontWeight: !selectedCatId ? 'bold' : '500', fontSize: '14px', transition: '0.3s' }}>
                    View All Products
                </li>

                {/* Dynamic Category List (Logic A Data) */}
                {categories.map(cat => (
                    <li key={cat.categoryId} className="mb-1">
                        <div onClick={() => handleCategoryClick(cat)}
                            className="d-flex justify-content-between align-items-center p-2 rounded"
                            style={{ 
                                cursor: 'pointer', 
                                color: selectedCatId === String(cat.categoryId) ? '#FFC35B' : '#555', 
                                fontWeight: selectedCatId === String(cat.categoryId) ? '800' : 'normal',
                                transition: 'color 0.2s' 
                            }}>
                            <span style={{ fontSize: '15px' }}>{cat.categoryName}</span>
                            {cat.subcategoryList && cat.subcategoryList.length > 0 && (
                                <span style={{fontSize: '10px', opacity: 0.5}}>{selectedCatId === String(cat.categoryId) ? '▼' : '▶'}</span>
                            )}
                        </div>
                        
                        {/* Subcategories */}
                        {selectedCatId === String(cat.categoryId) && cat.subcategoryList && (
                            <ul className="list-unstyled ms-3 mt-1 ps-2" style={{ borderLeft: '2px solid #eee' }}>
                                {cat.subcategoryList.map(sub => (
                                    <li key={sub.subCategoryId} onClick={(e) => handleSubCategoryClick(e, cat, sub)}
                                        style={{ 
                                            cursor: 'pointer', padding: '5px 8px', fontSize: '13px', 
                                            color: selectedSubCatId === String(sub.subCategoryId) ? '#333' : '#888', 
                                            fontWeight: selectedSubCatId === String(sub.subCategoryId) ? 'bold' : 'normal', 
                                            backgroundColor: selectedSubCatId === String(sub.subCategoryId) ? '#fff8e1' : 'transparent', 
                                            borderRadius: '4px' 
                                        }}>
                                        {sub.subCategoryName}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </li>
                ))}
            </ul>
          </div>
        </div>

        {/* ================= MAIN GRID ================= */}
        <div className="col-lg-9">
           
           {/* HEADER WITH TAGS (UI Source B Style) */}
           <div className="d-flex flex-wrap align-items-center gap-2 mb-4 p-3 bg-white border rounded shadow-sm">
                <h4 style={{ fontFamily: 'serif', margin: 0, paddingRight: '20px', borderRight: '1px solid #ddd', color: '#333' }}>
                    {selectedCatName === "All" ? "Shop All" : selectedCatName}
                </h4>

                {/* Subcategory Tag */}
                {selectedSubName && (
                    <span className="badge bg-light text-dark border d-flex align-items-center shadow-sm" style={{ padding: '8px 12px', fontSize: '13px' }}>
                        {selectedSubName}
                        <span onClick={() => { setSelectedSubCatId(null); setSelectedSubName(""); fetchFilteredProducts(selectedCatId, null, priceRange); }}
                            style={{ marginLeft: '8px', cursor: 'pointer', color: '#aaa', fontSize: '14px' }}>&times;</span>
                    </span>
                )}

                {/* Price Tag */}
                {priceRange < 5000 && (
                    <span className="badge bg-light text-dark border d-flex align-items-center shadow-sm" style={{ padding: '8px 12px', fontSize: '13px' }}>
                        Under ₹{priceRange}
                        <span onClick={() => { setPriceRange(5000); fetchFilteredProducts(selectedCatId, selectedSubCatId, 5000); }}
                            style={{ marginLeft: '8px', cursor: 'pointer', color: '#aaa', fontSize: '14px' }}>&times;</span>
                    </span>
                )}

                <span className="ms-auto text-muted small">{products.length} Products Found</span>
           </div>

           {/* PRODUCT GRID */}
           {loading ? (
             <div className="text-center py-5" style={{ minHeight: '400px' }}>
                <div className="spinner-border" style={{color: '#FFC35B'}} role="status"></div>
                <p className="mt-2 text-muted">Loading products...</p>
             </div>
           ) : products.length === 0 ? (
             <div className="col-12 text-center py-5 bg-white rounded border">
                <img src="/assets/images/no-data.png" alt="No Data" style={{ width: '80px', opacity: 0.3, marginBottom: '20px' }} onError={(e) => e.target.style.display='none'} />
                <h5 className="text-muted">No products found.</h5>
                <button className="btn btn-dark mt-3 px-4 rounded-pill" onClick={handleReset}>Reset Filters</button>
             </div>
           ) : (
             <div className="row">
               {products.map(p => (
                 <ProductCard key={p.productId} product={p} />
               ))}
             </div>
           )}

        </div>
      </div>
    </div>
  );
};

export default DivineDepotShop;